// src/services/excelImportService.js
// Import de matchs depuis fichier Excel (feuille "Prise de note")
// Format : J1 en B1, J2 en C1, tournoi en D2, date en D3, premier serveur en B3
// Points à partir de la ligne 6 : colonne B = points J1, colonne C = points J2

import * as XLSX from 'xlsx';
import { TennisScoreEngine } from '../TennisScoreEngine';
import { supabase } from '../supabaseClient';

// ─── Décodage d'un code Excel en pointData TenStats ──────────────────────────
/**
 * Transforme un code agrégé Excel (ex: "VS5", "RC3F", "S4") en pointData TenStats
 * Structure du code :
 *   - 1er caractère : famille (S, T, C, R, V)
 *   - Pour S et T : caractère suivant = sous-type (zone 1-6), puis F si faute
 *   - Pour C, R, V : caractère(s) suivants = sous-type, puis chiffre = nbFrappes, puis F si faute
 */
const decodeExcelCode = (code) => {
  if (!code) return null;
  const str = String(code).trim();
  if (!str) return null;

  const famille = str[0]; // S, T, C, R, V
  let rest = str.slice(1);

  // Détecter faute (F en fin)
  let resultat = '';
  if (rest.endsWith('F')) {
    resultat = 'F';
    rest = rest.slice(0, -1);
  }

  // Pour S et T : pas de nbFrappes, le reste est le sous-type (zone)
  // Pour C, R, V : dernier chiffre = nbFrappes, avant = sous-type
  let sousType = '';
  let nbFrappes = null;

  if (famille === 'S' || famille === 'T') {
    sousType = rest; // ex: '4', '3', '1'
    nbFrappes = null;
  } else {
    if (rest && rest[rest.length - 1].match(/\d/)) {
      nbFrappes = parseInt(rest[rest.length - 1], 10);
      sousType = rest.slice(0, -1);
    } else {
      sousType = rest;
    }
  }

  return {
    familleCode:   famille,
    sousTypeCode:  sousType,
    nbFrappesCode: nbFrappes,
    resultatCode:  resultat,
    // Champs non disponibles dans l'Excel simplifié
    serviceReussite:  null,
    zoneCode:         null,
    effetCode:        null,
    fauteDirecteCode: null,
    positionServeur:  null,
    coteService:      null,
  };
};

// ─── Génération du codePoint (format TenStats) ───────────────────────────────
/**
 * Génère le codePoint stocké dans engineState.points[].codePoint
 * Format : "famillesousTypeNbFrappesResultat;serviceReussite;zone;effet;fauteDirecte"
 */
const generateCodePoint = (pointData) => {
  if (!pointData) return null;
  const { familleCode, sousTypeCode, nbFrappesCode, resultatCode } = pointData;

  let mainCode = familleCode || '';
  if (sousTypeCode) mainCode += sousTypeCode;
  if (nbFrappesCode) mainCode += nbFrappesCode;
  if (resultatCode === 'F') mainCode += 'F';

  // Optionnels vides (non disponibles dans l'Excel simplifié)
  return `${mainCode};;;`;
};

// ─── Parsing de la date Excel ─────────────────────────────────────────────────
const parseExcelDate = (val) => {
  if (!val) return null;

  // Cas 1 : objet Date JavaScript (raw: true avec cellDates)
  if (val instanceof Date && !isNaN(val.getTime())) {
    const y = val.getFullYear();
    const m = String(val.getMonth() + 1).padStart(2, '0');
    const d = String(val.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Cas 2 : string ISO "2025-09-03T00:00:00.000Z"
  if (typeof val === 'string' && val.includes('T')) {
    const date = new Date(val);
    if (!isNaN(date.getTime())) {
      const y = date.getUTCFullYear();
      const m = String(date.getUTCMonth() + 1).padStart(2, '0');
      const d = String(date.getUTCDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }

  if (typeof val === 'string') {
    const trimmed = val.trim();

    // Cas 3 : format américain MM/JJ/AA ou MM/JJ/AAAA (retourné par XLSX.js)
    const matchUS2 = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
    if (matchUS2) {
      const [, m, d, yy] = matchUS2;
      const y = parseInt(yy) < 50 ? `20${yy}` : `19${yy}`;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const matchUS4 = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (matchUS4) {
      const [, m, d, y] = matchUS4;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }

    // Cas 4 : format français JJ/MM/AAAA
    const matchFR = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (matchFR) {
      const [, d, m, y] = matchFR;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }

    // Cas 5 : format ISO AAAA-MM-JJ déjà correct
    const matchISO = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (matchISO) return trimmed;
  }

  // Cas 6 : numéro de série Excel
  if (typeof val === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    const date = new Date(excelEpoch.getTime() + val * 86400000);
    if (!isNaN(date.getTime())) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }

  return null;
};

// ─── Import principal ─────────────────────────────────────────────────────────
/**
 * Importe un match depuis un fichier Excel
 * @param {File} file - Fichier .xlsx uploadé
 * @param {string} userId - ID de l'utilisateur connecté
 * @param {Object} options - Options supplémentaires
 *   - matchFormats: format des sets ex: ["STD","STD","NON","NON","NON"]
 *   - formatMatch: "BEST_OF_3" ou "BEST_OF_5"
 * @returns {{ success, match, pointsCount, error }}
 */
export const importMatchFromExcel = async (file, userId, options = {}) => {
  try {
    console.log('📥 Import Excel démarré:', file.name);

    // ── 1. Lire le fichier Excel ──────────────────────────────────────────────
    const buffer = await file.arrayBuffer();
    const wb = XLSX.read(buffer, { type: 'array', cellDates: true });

    const sheetName = 'Prise de note';
    if (!wb.SheetNames.includes(sheetName)) {
      return { success: false, error: `Feuille "${sheetName}" introuvable dans le fichier.` };
    }

    const ws = wb.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null, raw: false });

    // ── 2. Extraire les métadonnées du match ──────────────────────────────────
    // Ligne 1 (index 0) : [null, J1, J2, scoreFinal]
    // Ligne 2 (index 1) : [null, null, classementJ2, tournoi]
    // Ligne 3 (index 2) : [null, premierServeur, null, date]

    const nomJ1       = data[0]?.[1] ? String(data[0][1]).trim() : null;
    const nomJ2       = data[0]?.[2] ? String(data[0][2]).trim() : null;
    const tournoi     = data[1]?.[3] ? String(data[1][3]).trim() : null;
    const dateRaw     = data[2]?.[3] || null;
    const serveurRaw  = data[2]?.[1] ? String(data[2][1]).trim() : null;

    if (!nomJ1 || !nomJ2) {
      return { success: false, error: 'Noms des joueurs introuvables (cellules B1 et C1).' };
    }

    // Premier serveur : J1 ou J2 obligatoire en cellule B3
    if (!serveurRaw || !['J1', 'J2'].includes(serveurRaw)) {
      return {
        success: false,
        error: `Cellule B3 manquante ou invalide ("${serveurRaw || 'vide'}"). Indiquez J1 ou J2 pour le premier serveur.`
      };
    }
    const premierServeur = serveurRaw;
    console.log(`👤 J1: ${nomJ1}, J2: ${nomJ2}`);
    console.log(`🎾 Premier serveur: ${premierServeur}`);
    console.log(`🏆 Tournoi: ${tournoi}`);

    const dateMatch = parseExcelDate(dateRaw);
    console.log(`📅 Date: ${dateMatch}`);

    // ── 3. Lire les points (à partir de la ligne 6, index 5) ─────────────────
    const pointsRaw = [];
    for (let i = 5; i < data.length; i++) {
      const row = data[i];
      if (!row) continue;
      const codeJ1 = row[1] ? String(row[1]).trim() : null;
      const codeJ2 = row[2] ? String(row[2]).trim() : null;
      if (!codeJ1 && !codeJ2) continue; // Ligne vide

      if (codeJ1) {
        pointsRaw.push({ winner: 'J1', code: codeJ1 });
      } else {
        pointsRaw.push({ winner: 'J2', code: codeJ2 });
      }
    }

    if (pointsRaw.length === 0) {
      return { success: false, error: 'Aucun point trouvé dans la feuille "Prise de note".' };
    }

    console.log(`📝 ${pointsRaw.length} points lus depuis l'Excel`);

    // ── 4. Initialiser le moteur TennisScoreEngine ────────────────────────────
    const {
      matchFormats = ['STD', 'STD', 'NON', 'NON', 'NON'],
      formatMatch  = 'BEST_OF_3',
    } = options;

    const engine = new TennisScoreEngine({
      matchFormats,
      formatMatch,
      premierServeur,
    });

    // ── 5. Alimenter le moteur point par point ────────────────────────────────
    let pointsImported = 0;
    for (const { winner, code } of pointsRaw) {
      if (engine.matchFinished) {
        console.log(`⚠️ Match terminé après ${pointsImported} points, ${pointsRaw.length - pointsImported} points ignorés`);
        break;
      }

      const pointData = decodeExcelCode(code);
      const codePoint = generateCodePoint(pointData);

      engine.addPoint(winner, pointData, codePoint);
      pointsImported++;
    }

    console.log(`✅ ${pointsImported} points injectés dans le moteur`);

    // ── 6. Récupérer l'état final du moteur ───────────────────────────────────
    const engineState = engine.saveState();
    const currentScore = engine.getCurrentScore();

    console.log('🏁 Score final:', currentScore.sets);
    console.log('🏆 Winner:', currentScore.winner);

    // ── 7. Construire l'objet match complet ───────────────────────────────────
    const matchId = crypto.randomUUID();
    const now = new Date().toISOString();

    // playerDisplay.js utilise joueur1Nom pour le nom complet
    const j1 = { prenom: '', joueurNom: nomJ1 || '' };
    const j2 = { prenom: '', joueurNom: nomJ2 || '' };

    const match = {
      id:      matchId,
      matchId: matchId,
      userId:  userId,

      // Joueurs
      prenomJoueur1: j1.prenom,
      joueur1Nom:    j1.joueurNom,
      prenomJoueur2: j2.prenom,
      joueur2Nom:    j2.joueurNom,

      // Infos match
      dateMatch:    dateMatch,
      lieuMatch:    tournoi || '',
      tournoiNom:   tournoi || '',
      formatMatch:  formatMatch,
      matchFormats: matchFormats,
      premierServeur: premierServeur,
      status:       'FINISHED',

      // Score et moteur
      engineState:  engineState,
      currentScore: currentScore,

      // Métadonnées
      source:    'excel_import',
      createdAt: now,
      updatedAt: now,
    };

    // ── 8. Insérer dans Supabase et récupérer l'ID définitif ────────────────
    let finalMatchId = matchId;
    console.log('☁️ Insertion dans Supabase...');
    try {
      const { data: supabaseData, error: supabaseError } = await supabase
        .from('matches')
        .insert({
          userId:        userId,
          joueur1Nom:    j1.joueurNom,
          joueur2Nom:    j2.joueurNom,
          prenomJoueur1: j1.prenom,
          prenomJoueur2: j2.prenom,
          dateMatch:     dateMatch,
          tournoiNom:    tournoi || null,
          formatMatch:   formatMatch,
          premierServeur: premierServeur,
          currentScore:  currentScore,
          engineState:   engineState,
          status:        'FINISHED',
        })
        .select()
        .single();

      if (supabaseError) throw supabaseError;

      // Utiliser l'ID Supabase comme ID définitif
      finalMatchId = supabaseData.id;
      match.id = finalMatchId;
      match.matchId = finalMatchId;
      console.log('✅ Match inséré dans Supabase avec ID:', finalMatchId);
    } catch (supabaseError) {
      console.warn('⚠️ Sync Supabase échouée:', supabaseError.message);
    }

    // ── 9. Sauvegarder en localStorage avec l'ID définitif ───────────────────
    localStorage.setItem(`match_${finalMatchId}`, JSON.stringify(match));
    const existingList = JSON.parse(localStorage.getItem('matches') || '[]');
    const filteredList = existingList.filter(m => m.id !== matchId && m.matchId !== matchId);
    filteredList.unshift({
      id:            finalMatchId,
      matchId:       finalMatchId,
      userId:        userId,
      joueur1Nom:    j1.joueurNom,
      prenomJoueur1: j1.prenom,
      joueur2Nom:    j2.joueurNom,
      prenomJoueur2: j2.prenom,
      dateMatch:     dateMatch,
      lieuMatch:     tournoi || '',
      formatMatch:   formatMatch,
      currentScore:  currentScore,
      source:        'excel_import',
      synced:        true,
      needsSync:     false,
      createdAt:     now,
      updatedAt:     now,
    });
    localStorage.setItem('matches', JSON.stringify(filteredList));
    console.log('✅ Match sauvegardé en local avec ID définitif:', finalMatchId);

    console.log(`✅ Import terminé : ${pointsImported} points, match ${matchId}`);

    return {
      success:      true,
      match:        match,
      pointsCount:  pointsImported,
      currentScore: currentScore,
      finalMatchId: match.id,
    };

  } catch (err) {
    console.error('❌ Erreur import Excel:', err);
    return { success: false, error: err.message || 'Erreur inconnue lors de l\'import.' };
  }
};